import { useEffect, useRef, useState } from "react";

export interface PageHighlight {
  key: string;
  label: string;
  top: number;
  left: number;
  bottom: number;
  right: number;
  active?: boolean;
}

export interface NormalizedRect {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

/**
 * Muestra una página (imagen) rotable que siempre se ajusta al ancho del
 * contenedor, también cuando se gira 90°/270°. Opcionalmente pinta zonas
 * resaltadas y permite seleccionar un rectángulo con el ratón.
 */
const RotatablePage = ({
  src,
  alt,
  rotation,
  highlights,
  selectable,
  onSelectRect,
  zoom = 1,
  fitMode = "width",
  fitHeight,
}: {
  zoom?: number;
  fitMode?: "width" | "height";
  /** Alto disponible (px) para «Ajustar al alto». */
  fitHeight?: number;
  src: string;
  alt: string;
  rotation: number;
  highlights?: PageHighlight[];
  selectable?: boolean;
  onSelectRect?: (rect: NormalizedRect) => void;
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const upd = () => setWidth(el.clientWidth);
    upd();
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const activeKey = highlights?.find((h) => h.active)?.key;
  useEffect(() => {
    if (activeKey) activeRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [activeKey]);

  const rotated = rotation % 180 !== 0;
  let boxH: number | undefined;
  let imgW: number | undefined;
  let imgH: number | undefined;
  let boxW: number | undefined;
  if (nat && width) {
    const byHeight = fitMode === "height" && fitHeight && fitHeight > 50;
    if (rotated) {
      // Caja visible: ancho = imgH, alto = imgW
      imgH = byHeight ? (nat.h / nat.w) * fitHeight! : width;
      imgW = (nat.w / nat.h) * imgH;
    } else {
      imgW = byHeight ? (nat.w / nat.h) * fitHeight! : width;
      imgH = (nat.h / nat.w) * imgW;
    }
    imgW *= zoom;
    imgH *= zoom;
    boxW = rotated ? imgH : imgW;
    boxH = rotated ? imgW : imgH;
  }

  const pos = (e: React.PointerEvent) => {
    const r = pageRef.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };
  const canSelect = !!selectable && rotation === 0 && !!onSelectRect;

  return (
    <div ref={wrapRef} className="w-full shrink-0">
    <div className="relative mx-auto" style={{ height: boxH, width: boxW }}>
      <div
        ref={pageRef}
        className={canSelect ? "cursor-crosshair select-none touch-none" : undefined}
        style={{
          width: imgW,
          height: imgH,
          position: nat ? "absolute" : "static",
          top: "50%",
          left: "50%",
          transform: nat ? `translate(-50%, -50%) rotate(${rotation}deg)` : undefined,
          transformOrigin: "center center",
          transition: "transform 0.2s ease",
        }}
        onPointerDown={canSelect ? (e) => {
          e.preventDefault();
          (e.target as Element).setPointerCapture?.(e.pointerId);
          const p = pos(e);
          setDrag({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
        } : undefined}
        onPointerMove={canSelect && drag ? (e) => {
          const p = pos(e);
          setDrag((d) => (d ? { ...d, x1: p.x, y1: p.y } : d));
        } : undefined}
        onPointerUp={canSelect && drag ? () => {
          const rect = {
            top: Math.min(drag.y0, drag.y1),
            bottom: Math.max(drag.y0, drag.y1),
            left: Math.min(drag.x0, drag.x1),
            right: Math.max(drag.x0, drag.x1),
          };
          setDrag(null);
          if (rect.right - rect.left > 0.005 && rect.bottom - rect.top > 0.003) onSelectRect!(rect);
        } : undefined}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          onLoad={(e) => {
            const t = e.currentTarget;
            setNat({ w: t.naturalWidth, h: t.naturalHeight });
          }}
          className="rounded-lg border w-full h-full"
          style={{ maxWidth: "none" }}
        />
        {highlights?.map((h) => (
          <div
            key={h.key}
            ref={h.active ? activeRef : undefined}
            className={`absolute pointer-events-none rounded-sm ${
              h.active ? "bg-yellow-300/50" : "bg-yellow-200/30"
            }`}
            style={{
              top: `calc(${h.top * 100}% - 2px)`,
              left: `calc(${h.left * 100}% - 2px)`,
              width: `calc(${(h.right - h.left) * 100}% + 4px)`,
              height: `calc(${(h.bottom - h.top) * 100}% + 4px)`,
            }}
          />

        ))}
        {drag && (
          <div
            className="absolute pointer-events-none border-2 border-dashed border-accent-foreground bg-accent/30"
            style={{
              top: `${Math.min(drag.y0, drag.y1) * 100}%`,
              left: `${Math.min(drag.x0, drag.x1) * 100}%`,
              width: `${Math.abs(drag.x1 - drag.x0) * 100}%`,
              height: `${Math.abs(drag.y1 - drag.y0) * 100}%`,
            }}
          />
        )}
      </div>
    </div>
    </div>
  );
};

export default RotatablePage;
